// src/hooks/useProcessData.ts - Korrigerad för bättre kategorihantering
import { useState, useEffect, useCallback } from "react";
import { ProcessItem } from "@/types";
import { processHierarchicalNumbers } from "@/utils/processUtils";

export const useProcessData = (
  accessToken: string, 
  businessArea: string | null = null,
  category: string | null = null
) => {
  const [data, setData] = useState<ProcessItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [categories, setCategories] = useState<string[]>([]);

  const fetchData = useCallback(async () => {
    if (!accessToken) {
      setLoading(false);
      setError("No access token provided");
      return;
    }

    // Log params för debugging
    console.log("useProcessData called with:", { businessArea, category });

    try {
      setLoading(true);
      setError("");

      // Build select part of API call including Kategori
      const selectFields = [
        "Title", "field_0", "field_2", "field_3", "field_4", 
        "Listordning", "Viktigt", "Detaljeradbeskrivning",
        "Deltagareochansvar1", "Deltagareochansvar2", "Delavbolag", "Kategori"
      ].join(",");

      // Build filter part based on businessArea and category
      let filterPart = "";
      
      if (businessArea && businessArea !== "Alla") {
        // Filter on business area
        filterPart = `&$filter=Delavbolag eq '${encodeURIComponent(businessArea)}'`;
        
        // If category is specified, add it to the filter
        if (category) {
          filterPart += ` and Kategori eq '${encodeURIComponent(category)}'`;
        }
      }

      // Build the complete API URL
      const apiUrl =
        "https://solkompaniet365.sharepoint.com/sites/SolkompanietProcesser" +
        `/_api/web/lists/getbytitle('Process Viewer CMS')/items` +
        `?$select=${selectFields}` +
        `&$orderby=Listordning asc` +
        filterPart;

      console.log("API URL:", apiUrl);
      console.log("Fetching process data with params:", { businessArea, category });

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Accept: "application/json;odata=nometadata",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const json = await response.json();
      
      // Handle empty response
      if (!json.value) {
        console.warn("No rows found in data");
        setData([]);
        setCategories([]);
        setLoading(false);
        return;
      }

      console.log(`Received ${json.value.length} items from API`);

      // Handle category landing page - check if we need to extract categories
      if (businessArea && category === null) {
        // First, extract unique categories
        const uniqueCategories = Array.from(new Set(
          json.value
            .filter((item: ProcessItem) => item.Kategori && item.Kategori.trim() !== "")
            .map((item: ProcessItem) => item.Kategori?.trim())
        )).sort() as string[];
        
        setCategories(uniqueCategories);
        console.log(`Found ${uniqueCategories.length} unique categories`);
        
        // Filter items without a category - for uncategorized items list
        const itemsWithoutCategory = json.value.filter((item: ProcessItem) => 
          !item.Kategori || item.Kategori.trim() === ""
        );
        
        console.log(`Found ${itemsWithoutCategory.length} items without category`);
        
        // Process and set data for uncategorized items
        if (itemsWithoutCategory.length > 0) {
          const processedData = processHierarchicalNumbers(itemsWithoutCategory);
          setData(processedData);
        } else {
          setData([]);
        }
        
        setLoading(false);
        return;
      }

      // For standard view (main process viewer) with or without category
      let itemsToProcess = [...json.value];
      
      // Sort data by Listordning
      const sortedData = itemsToProcess.sort((a: ProcessItem, b: ProcessItem) => {
        const aOrder = a.Listordning !== undefined ? Number(a.Listordning) : 9999;
        const bOrder = b.Listordning !== undefined ? Number(b.Listordning) : 9999;
        return aOrder - bOrder;
      });

      // Process hierarchical numbers and set the data
      const processedData = processHierarchicalNumbers(sortedData);
      console.log(`Processed ${processedData.length} items with hierarchical numbers`);
      
      setData(processedData);
      
    } catch (err) {
      console.error("API error:", err);
      setError(typeof err === "object" && err !== null && "message" in err 
        ? String(err.message) 
        : "An unknown error occurred");
      
      // Return empty data on error
      setData([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, businessArea, category]);

  useEffect(() => {
    if (accessToken) {
      fetchData();
    }
  }, [accessToken, businessArea, category, fetchData]);

  return { data, loading, error, refetch: fetchData, categories };
};

export default useProcessData;
